import {
  ApolloClient,
  ApolloLink,
  DefaultOptions,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  split,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { RetryLink } from '@apollo/client/link/retry'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { SubscriptionClient } from 'subscriptions-transport-ws'

import { generateTraceId } from '~/client/src/shared/utils/traceId'

import { createRefreshOn401Link } from './refreshOn401Link'

export * from './types.generated'

const cacheDefaultOptions: DefaultOptions = {
  query: { fetchPolicy: 'cache-first' },
  mutate: { fetchPolicy: 'no-cache' },
  watchQuery: { fetchPolicy: 'no-cache' },
}

const omitTypename = (key: string, value: any) =>
  key === '__typename' ? undefined : value

export function createGraphClient(
  getAccessToken: () => Promise<string>,
  forceRefreshAccessToken: () => Promise<string>,
  url?: string,
  onSubscriptionsReconnected?: () => void,
): ApolloClient<NormalizedCacheObject> {
  // Tenantless mode
  if (!url) {
    return new ApolloClient({
      uri: null,
      cache: new InMemoryCache({
        typePolicies: {
          SitePermit: {
            fields: {
              fields: {
                merge(_existing, incoming: any[]) {
                  return incoming // overwrite completely
                },
              },
            },
          },
        },
      }),
      defaultOptions: cacheDefaultOptions,
    })
  }

  // Match the page's scheme: https pages need wss/https (mixed-content
  // rules), http dev servers (localhost AND LAN-IP device testing) need
  // ws/http. Keying off the hostname forced https for any non-localhost
  // url, which broke GraphQL over a LAN IP on a plain-http dev server.
  const secureSuffix = location.protocol === 'https:' ? 's' : ''

  const subscriptionClient = new SubscriptionClient(
    `ws${secureSuffix}://${url}/graphql`,
    {
      lazy: true,
      reconnect: true,
      connectionParams: async () => {
        const accessToken = await getAccessToken()
        return {
          authorization: `Bearer ${accessToken}`,
        }
      },
    },
  )

  // A reconnect re-sends the subscriptions, but nothing that changed while the
  // socket was down is ever delivered, so let the app re-read what it needs.
  if (onSubscriptionsReconnected) {
    subscriptionClient.onReconnected(onSubscriptionsReconnected)
  }

  const wsLink = new WebSocketLink(subscriptionClient)

  // Resources WebSocket link using graphql-transport-ws protocol
  const resourcesWsLink = (() => {
    const client = createClient({
      url: `ws${secureSuffix}://${url}/resources`,
      webSocketImpl: typeof window !== 'undefined' ? WebSocket : undefined,
      lazy: true,
      retryAttempts: 50,
      connectionParams: async () => {
        const accessToken = await getAccessToken()
        return {
          authorization: `Bearer ${accessToken}`,
        }
      },
    })

    return new GraphQLWsLink(client)
  })()

  const authLink = setContext(async (_, { headers }) => {
    const accessToken = await getAccessToken()
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${accessToken}`,
      },
    }
  })

  const refreshOn401Link = createRefreshOn401Link(forceRefreshAccessToken)

  const traceLink = new ApolloLink((operation, forward) => {
    const traceId = generateTraceId()

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'X-Trace-Id': traceId,
      },
    }))

    return forward(operation)
  })

  const middleWareLink = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      operation.variables = JSON.parse(
        JSON.stringify(operation.variables),
        omitTypename,
      )
    }

    return forward(operation)
  })

  // Skip RetryLink for auth-shaped errors so refreshOn401Link (which sits
  // above) sees the 401 immediately instead of waiting through 5 retries
  // with exponential backoff on a token the server has already rejected.
  // Non-auth errors keep the default retry behaviour.
  const retryLink = new RetryLink({
    attempts: {
      retryIf: error => {
        if (!error) return false
        const status = (error as { statusCode?: number }).statusCode
        if (status === 401 || status === 403) return false
        return true
      },
    },
  })

  const httpLink = refreshOn401Link
    .concat(authLink)
    .concat(traceLink)
    .concat(middleWareLink)
    .concat(retryLink)
    .concat(
      new HttpLink({
        uri: `http${secureSuffix}://${url}/graphql`,
      }),
    )

  // Resources endpoint link (no batching - backend doesn't support it)
  const resourcesHttpLink = refreshOn401Link
    .concat(authLink)
    .concat(traceLink)
    .concat(middleWareLink)
    .concat(retryLink)
    .concat(
      new HttpLink({
        uri: `http${secureSuffix}://${url}/resources`,
      }),
    )

  // Route queries based on context
  const routedLink = split(
    operation => {
      return operation.getContext().endpoint === 'resources'
    },
    resourcesHttpLink, // Route to resources endpoint
    httpLink, // Route to main endpoint
  )

  // Route WebSocket connections based on context
  const routedWsLink = split(
    operation => {
      return operation.getContext().endpoint === 'resources'
    },
    resourcesWsLink, // Route to resources WebSocket
    wsLink, // Route to main WebSocket
  )

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      )
    },
    routedWsLink,
    routedLink,
  )

  const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
    link: splitLink,
    connectToDevTools: true,
    cache: new InMemoryCache({
      typePolicies: {
        SitePermit: {
          fields: {
            fields: {
              merge(_existing, incoming: any[]) {
                return incoming // overwrite completely
              },
            },
          },
        },
      },
      possibleTypes: {
        PermitFieldInterface: [
          'ObjectIdPermitField',
          'LocationPermitField',
          'StringPermitField',
          'AttachmentPermitField',
          'ChecklistAnswerItemPermitField',
          'DateTimePermitField',
          'MeasurePermitField',
          'MaterialFormField',
          'RichTextFormField',
          'TablePermitField',
          'SignaturePermitField',
        ],
        Location: [
          'Area',
          'Building',
          'Gate',
          'Integration',
          'InteriorDoor',
          'InteriorPath',
          'Level',
          'LogisticsObject',
          'Material',
          'OffloadingEquipment',
          'ProjectLocation',
          'Route',
          'Sensor',
          'Staging',
          'VerticalObject',
          'Zone',
        ],
      },
    }),
    defaultOptions: cacheDefaultOptions,
  })

  client.addResolvers({
    Date: {
      Date: rootValue => rootValue,
    },
  })

  return client
}
